import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { FileText, Clock, Search, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { format } from "date-fns";

const statusColorMap: Record<string, string> = {
  "In Committee": "bg-accent/10 text-accent border-accent/20",
  "Passed Senate": "bg-issue-climate/10 text-issue-climate border-issue-climate/20",
  "Passed House": "bg-issue-justice/10 text-issue-justice border-issue-justice/20",
  "Introduced": "bg-issue-education/10 text-issue-education border-issue-education/20",
};

const STATUSES = ["In Committee", "Passed Senate", "Passed House", "Introduced"];
const AREAS = ["Housing", "Climate", "Education", "Justice", "Health"];

const LegislationTracker = () => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [areaFilter, setAreaFilter] = useState<string | null>(null);
  const [countyFilter, setCountyFilter] = useState<string | null>(null);

  const { data: bills, isLoading } = useQuery({
    queryKey: ["legislation"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("legislation")
        .select("*")
        .order("date", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const counties = useMemo(() => {
    if (!bills) return [];
    return [...new Set(bills.map((b) => b.county).filter(Boolean))] as string[];
  }, [bills]);

  const filtered = useMemo(() => {
    if (!bills) return [];
    return bills.filter((bill) => {
      if (search && !bill.title.toLowerCase().includes(search.toLowerCase()) && !bill.bill_id.toLowerCase().includes(search.toLowerCase())) return false;
      if (statusFilter && bill.status !== statusFilter) return false;
      if (areaFilter && bill.area !== areaFilter) return false;
      if (countyFilter && bill.county !== countyFilter) return false;
      return true;
    });
  }, [bills, search, statusFilter, areaFilter, countyFilter]);

  const hasFilters = search || statusFilter || areaFilter || countyFilter;

  return (
    <section className="py-24 bg-muted/30 relative">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-col md:flex-row md:items-end justify-between mb-12"
        >
          <div>
            <span className="text-primary text-sm font-semibold tracking-wider uppercase">Live Tracker</span>
            <h2 className="font-display text-3xl md:text-5xl font-bold mt-3 mb-4">
              Alabama Legislation
            </h2>
            <p className="text-muted-foreground text-lg max-w-xl">
              Key Alabama bills we're tracking across all focus areas, updated in real time.
            </p>
          </div>
          <button className="mt-4 md:mt-0 text-primary font-semibold text-sm hover:underline flex items-center gap-1">
            View all bills <FileText className="h-4 w-4" />
          </button>
        </motion.div>

        {/* Search & Filters */}
        <div className="mb-6 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by bill title or ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-card border-border"
            />
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mr-1">Status:</span>
            {STATUSES.map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(statusFilter === s ? null : s)}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                  statusFilter === s
                    ? statusColorMap[s]
                    : "bg-card border-border text-muted-foreground hover:border-primary/40"
                }`}
              >
                {s}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mr-1">Area:</span>
            {AREAS.map((a) => (
              <button
                key={a}
                onClick={() => setAreaFilter(areaFilter === a ? null : a)}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                  areaFilter === a
                    ? "bg-primary/10 text-primary border-primary/30"
                    : "bg-card border-border text-muted-foreground hover:border-primary/40"
                }`}
              >
                {a}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mr-1">County:</span>
            {counties.map((c) => (
              <button
                key={c}
                onClick={() => setCountyFilter(countyFilter === c ? null : c)}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                  countyFilter === c
                    ? "bg-primary/10 text-primary border-primary/30"
                    : "bg-card border-border text-muted-foreground hover:border-primary/40"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {hasFilters && (
            <button
              onClick={() => { setSearch(""); setStatusFilter(null); setAreaFilter(null); setCountyFilter(null); }}
              className="text-xs text-primary hover:underline flex items-center gap-1"
            >
              <X className="h-3 w-3" /> Clear all filters
            </button>
          )}
        </div>

        <div className="bg-card border border-border rounded-lg overflow-hidden shadow-card">
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 border-b border-border text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            <div className="col-span-1">ID</div>
            <div className="col-span-3">Bill Title</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-1">Area</div>
            <div className="col-span-2">County</div>
            <div className="col-span-1">Date</div>
            <div className="col-span-2">Sponsors</div>
          </div>

          {isLoading
            ? Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-border">
                  <Skeleton className="col-span-1 h-5" />
                  <Skeleton className="col-span-3 h-5" />
                  <Skeleton className="col-span-2 h-5" />
                  <Skeleton className="col-span-1 h-5" />
                  <Skeleton className="col-span-2 h-5" />
                  <Skeleton className="col-span-1 h-5" />
                  <Skeleton className="col-span-2 h-5" />
                </div>
              ))
            : filtered.length === 0 ? (
                <div className="px-6 py-12 text-center text-muted-foreground">
                  No bills match your filters. Try adjusting your search criteria.
                </div>
              )
            : filtered.map((bill, i) => (
                <motion.div
                  key={bill.id}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-4 border-b border-border last:border-0 hover:bg-secondary/30 transition-colors cursor-pointer group"
                >
                  <div className="col-span-1 font-mono text-sm text-muted-foreground">{bill.bill_id}</div>
                  <div className="col-span-3 font-medium text-sm group-hover:text-primary transition-colors">{bill.title}</div>
                  <div className="col-span-2">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${statusColorMap[bill.status] || "bg-muted text-muted-foreground border-border"}`}>
                      {bill.status}
                    </span>
                  </div>
                  <div className="col-span-1">
                    <Badge variant="secondary" className="text-xs">{bill.area}</Badge>
                  </div>
                  <div className="col-span-2 text-sm text-muted-foreground">{bill.county || "—"}</div>
                  <div className="col-span-1 text-sm text-muted-foreground flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" /> {format(new Date(bill.date), "MMM d")}
                  </div>
                  <div className="col-span-2 text-sm text-muted-foreground">{bill.sponsors} co-sponsors</div>
                </motion.div>
              ))}
        </div>
      </div>
    </section>
  );
};

export default LegislationTracker;
